/**
 * DisciplinePill — canonical discipline color config and reusable pill component.
 *
 * Single source of truth for all discipline badge/pill styling throughout the app.
 * Conformsá to standard pill pattern: font-semibold text-xs rounded border with inline-flex layout.
 *
 * Usage (component):
 *   <DisciplinePill discipline="RN" />
 *   <DisciplinePill discipline="PT" className="opacity-70" />
 *
 * Usage (utilities):
 *   getDisciplineConfig('OT')          → full config object
 *   getDisciplinePillClassName('HHA')  → "bg-lime-50 text-lime-700 border-lime-200 font-semibold text-xs rounded border"
 *   getDisciplineCardClassName('RN')   → "border-sky-500 bg-sky-50"
 *
 * Colours: from the discipline registry (constants/disciplineRegistry.ts); Case Mgr is gray.
 */

import React from 'react';
import { disciplineForProviderType, getDisciplineColors, getProviderTypeEntry } from '../../../constants/disciplines';

// ─── Config types ─────────────────────────────────────────────────────────────

/** Full color / display config for a single discipline */
export interface DisciplineConfig {
  /** Light pill background (e.g. bg-blue-50) */
  bg: string;
  /** Pill text color (e.g. text-blue-700) */
  text: string;
  /** Pill border color (e.g. border-blue-200) */
  border: string;
  /** Card left-border accent — slightly darker (e.g. border-blue-500) */
  accent: string;
  /** Progress bar / solid fill (e.g. bg-blue-500) */
  bar: string;
  /** Canonical short display label shown in the pill */
  label: string;
}

// ─── Canonical map ────────────────────────────────────────────────────────────

/**
 * Phase 20A (D-12): colours come from the discipline registry palette (constants/disciplines.ts),
 * so pills, the Overview and the schedule agree. A provider type (LVN, PTA, COTA, SLPA) takes its
 * discipline's colour and shows its own code; long-form API strings map to the discipline.
 */
const LONG_FORMS: Record<string, string> = {
  PHYSICAL_THERAPY: 'PT', OCCUPATIONAL_THERAPY: 'OT', SPEECH_THERAPY: 'ST',
  SW: 'MSW', SOCIAL_WORK: 'MSW', HOME_HEALTH_AIDE: 'HHA',
};
/** Nursing shows as PDN, the agency's main nursing service */
const LABEL_OVERRIDES: Record<string, string> = { RN: 'PDN', SLP: 'ST' };

const DEFAULT_CONFIG: DisciplineConfig = {
  bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200',
  accent: 'border-gray-500', bar: 'bg-gray-500', label: '',
};

// ─── Utility functions ────────────────────────────────────────────────────────

/**
 * Look up the canonical config for a discipline code or provider type.
 * Normalises to uppercase; falls back to a gray default with the raw code as label.
 */
export function getDisciplineConfig(discipline: string): DisciplineConfig {
  if (!discipline) return { ...DEFAULT_CONFIG, label: discipline };
  const raw = discipline.trim().toUpperCase();
  if (raw === 'CASE_MGR' || raw === 'CASE_MANAGER') return { ...DEFAULT_CONFIG, label: 'Case Mgr' };
  const key = LONG_FORMS[raw] || raw;
  const resolved = disciplineForProviderType(key);
  if (!resolved) return { ...DEFAULT_CONFIG, label: discipline };
  const c = getDisciplineColors(resolved);
  const isProviderType = !!getProviderTypeEntry(key) && key !== resolved;
  const label = LABEL_OVERRIDES[key] || (isProviderType ? (getProviderTypeEntry(key)?.code || key) : resolved);
  return { bg: c.light, text: c.label, border: c.borderColor, accent: c.midBorder, bar: c.bar, label };
}

/**
 * Returns a combined Tailwind class string suitable for pill / badge elements.
 * Includes font, sizing, and border styling for consistency with standard pill pattern.
 *
 * @example
 *   className={`px-2 py-0.5 ${getDisciplinePillClassName('PT')}`}
 */
export function getDisciplinePillClassName(discipline: string): string {
  const BASE = 'font-semibold text-xs rounded border';
  const c = getDisciplineConfig(discipline);
  return `${c.bg} ${c.text} ${c.border} ${BASE}`;
}

/**
 * Returns a Tailwind class string for card discipline accents (light bg + left border):
 *   "border-{color}-500 bg-{color}-50"
 *
 * @example
 *   className={`p-2 rounded-md border-l-4 ${getDisciplineCardClassName('RN')}`}
 */
export function getDisciplineCardClassName(discipline: string): string {
  const c = getDisciplineConfig(discipline);
  return `${c.accent} ${c.bg}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface DisciplinePillProps {
  /** Discipline code: RN | PT | OT | ST | MSW | HHA | CASE_MGR … */
  discipline: string;
  /** Override display label (defaults to canonical label derived from config) */
  label?: string;
  /** Extra Tailwind classes appended to the root element */
  className?: string;
}

/**
 * DisciplinePill — canonical discipline badge component.
 *
 * Renders as: font-semibold · text-xs · rounded · border + discipline-specific
 * light background, colored text, and matching border.
 *
 * Standardized layout: inline-flex items-center · px-2 py-0.5
 */
export function DisciplinePill({
  discipline,
  label,
  className = '',
}: DisciplinePillProps) {
  const config = getDisciplineConfig(discipline);
  const displayLabel = label ?? (config.label || discipline);

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 ${getDisciplinePillClassName(discipline)} ${className}`.trim()}
    >
      {displayLabel}
    </span>
  );
}

export default DisciplinePill;
